import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import { devices, groups } from '../../../../stores';
import { scenes } from 'src/stores/lists/scenes';
import type { FlowNode, ActionItem } from '../../types/flow-types';
import { resolveNotificationCardPresentation } from '../../helpers/notificationDisplay';
import { resolveDeviceInStore } from '../../helpers/resolveDeviceInStore';
import { getConditionTemplate } from './conditionTemplates';
import { celsius_to_fahrenheit, tmp_units_from_settings } from 'src/helpers/interface_helpers';
import { capFirstChar } from 'src/helpers/utilities';

const _format = get(format);
/**
 * LabelGenerator - Generates human-readable labels for Flow nodes
 * Adapted from Scene system's label generation patterns
 */

/**
 * Get device name from device ID (supports base id vs suffixed $devices keys), with fallbacks.
 */
function getDeviceName(deviceId: string | null | undefined, channel?: number | string | null): string {
	if (!deviceId) return '';

	const devicesMap = get(devices);
	const ch = parseInt(String(channel ?? '0'), 10) || 0;
	const device = resolveDeviceInStore(devicesMap, deviceId, ch);

	if (!device) {
		return 'Unknown Device';
	}

	return device.name || deviceId;
}

/**
 * Get channel suffix for multi-channel devices
 */
function getChannelSuffix(channel: number | string | undefined): string {
	if (channel === undefined || channel === null || channel === 0 || channel === '0') {
		return '';
	}
	return ` (Channel ${channel})`;
}

/**
 * Format action value with appropriate unit
 */
function formatActionValue(
	actionName: string,
	value: any,
	deviceId?: string | null,
	channel?: number | string | null,
): string {
	switch (actionName) {
		case 'turn':
		case 'state':
			if (value === 'toggle') {
				return _format('scene_action_to_label_toggle', { default: 'toggle' });
			}
			return _format(`scene_action_to_label_${value}`, { default: value });

		case 'brightness':
		case 'white_channel':
		case 'roller position':
		case 'multi_cover_position':
		case 'position':
			return typeof value === 'number' && !Number.isNaN(value) ? `${value}%` : String(value);

		case 'temp':
		case 'ct':
			return `${value} K`;

		case 'roller state':
		case 'roller direction':
		case 'multi_cover_state':
		case 'opening_state':
			if (value === 'position') {
				return '';
			}
			return _format(`execute_${value}`, { default: value });

		case 'target_temp':
		case 'set_temp':
			return `${value}°C`; // Could be enhanced with F conversion

		case 'trv_target_temperature': {
			const devicesMap = get(devices);
			const ch = parseInt(String(channel ?? '0'), 10) || 0;
			const device = deviceId ? resolveDeviceInStore(devicesMap, deviceId, ch) : undefined;
			const units = tmp_units_from_settings(device || {});
			return units === '°C'
				? `${value} ${units}`
				: `${celsius_to_fahrenheit(Number(value))} ${units}`;
		}

		case 'valve_percent_position':
			return `${value}%`;

		default:
			return String(value);
	}
}

const rollerSetPositionTitle = (): string =>
	capFirstChar(_format('collapse_title_set_position', { default: 'set position' }));

const describeRollerCoverPercentPosition = (value: any): string => {
	const title = rollerSetPositionTitle();
	if (typeof value === 'number' && !Number.isNaN(value)) {
		return `${title} to ${value}%`;
	}
	return title;
};

/**
 * Get human-readable action description (verb + target format)
 */
function getActionDescription(
	actionName: string,
	value: any,
	deviceId?: string | null,
	channel?: number | string | null,
): string {
	switch (actionName) {
		case 'turn':
		case 'state':
			if (value === 'toggle') {
				return 'Toggle';
			}
			return value === 'on' ? 'Turn on' : 'Turn off';

		case 'brightness':
			return `Set brightness to ${value}%`;

		case 'white_channel':
			return `Set white to ${value}%`;

		case 'roller state':
		case 'roller direction':
		case 'multi_cover_state':
		case 'opening_state':
			if (value === 'position') {
				return rollerSetPositionTitle();
			}
			return _format(`execute_${value}`, { default: String(value) });

		case 'roller position':
		case 'multi_cover_position':
		case 'position':
			return describeRollerCoverPercentPosition(value);

		case 'temp':
		case 'ct':
			return `Set color temp to ${value}K`;

		case 'trv_target_temperature': {
			const propLabel = _format('prop_trv_target_temperature', {
				default: 'target temperature',
			});
			return `Set ${propLabel} to ${formatActionValue(actionName, value, deviceId, channel)}`;
		}

		case 'valve_percent_position': {
			const propLabel = rollerSetPositionTitle();
			return `${propLabel} to ${formatActionValue(actionName, value, deviceId, channel)}`;
		}

		case 'boost':
			if (value === 'on') {
				return _format('action_to_label_start_boost', {
					default: 'Start boost',
				});
			}
			return _format('boost_disabled', { default: 'Boost disabled' });

		case 'transition_duration': {
			const unit =
				+value === 1
					? _format('select_units_second', { default: 'second' })
					: _format('select_units_seconds', { default: 'seconds' });
			const title = _format('collapse_title_transition_duration', {
				default: 'Transition duration',
			});
			return `${title} ${value} ${unit}`;
		}

		default:
			return `Set ${actionName} to ${formatActionValue(actionName, value, deviceId, channel)}`;
	}
}

const ACTION_SEGMENT_SORT_KEY: Record<string, number> = {
	turn: 0,
	state: 0,
	brightness: 10,
	white_channel: 20,
	'roller state': 30,
	'roller direction': 30,
	multi_cover_state: 30,
	opening_state: 30,
	'roller position': 40,
	multi_cover_position: 40,
	position: 40,
	trv_target_temperature: 50,
	valve_percent_position: 60,
	target_temp: 70,
	set_temp: 70,
	boost: 80,
	transition_duration: 90,
	outputLimit: 100,
	bolt_state: 110,
};

const sortKeyForActionName = (name: string) =>
	ACTION_SEGMENT_SORT_KEY[name] !== undefined ? ACTION_SEGMENT_SORT_KEY[name] : 55;

/**
 * Human-readable segments for every configured device action (not only the first entry).
 * Groups RGB / correlated color channels into a single "Set color" line.
 */
const buildDeviceActionPreviewSegments = (
	actions: ActionItem[],
	deviceId: string,
	channel: number | string | null | undefined,
): string[] => {
	const nameSet = new Set(actions.map((a) => a.name));
	const segments: string[] = [];
	const consumed = new Set<string>();

	const turnAct = actions.find((a) => a.name === 'turn' || a.name === 'state');
	if (turnAct && turnAct.value !== undefined && turnAct.value !== null) {
		segments.push(getActionDescription(turnAct.name, turnAct.value, deviceId, channel));
		consumed.add(turnAct.name);
	}

	const hasRgb = ['r', 'g', 'b'].every((n) => nameSet.has(n));
	const tempEntry = actions.find((a) => a.name === 'temp' || a.name === 'ct');
	const tempVal = tempEntry?.value;

	if (hasRgb) {
		segments.push('Set color');
		['r', 'g', 'b', 'temp', 'ct'].forEach((n) => consumed.add(n));
	} else if (tempVal !== undefined && tempVal !== null && (nameSet.has('temp') || nameSet.has('ct'))) {
		segments.push(getActionDescription('temp', tempVal, deviceId, channel));
		consumed.add('temp');
		consumed.add('ct');
	}

	consumed.add('mode');

	const rest = actions.filter((a) => !consumed.has(a.name));
	rest.sort(
		(a, b) =>
			sortKeyForActionName(a.name) - sortKeyForActionName(b.name) ||
			actions.indexOf(a) - actions.indexOf(b),
	);

	const seenName = new Set<string>();
	for (const a of rest) {
		if (seenName.has(a.name)) continue;
		seenName.add(a.name);
		if (a.value === undefined || a.value === null) continue;
		if (a.name === 'boost' && a.value !== 'on') continue;
		segments.push(getActionDescription(a.name, a.value, deviceId, channel));
	}

	return segments;
};

const appendDelayAndTimerToActionPreview = (node: FlowNode, segments: string[]): void => {
	const delay = node.data.delay as { value?: number; units?: string } | undefined;
	if (delay && Number(delay.value) > 0) {
		const units = delay.units === 'min' ? 'minutes' : 'seconds';
		segments.push(`Delay ${delay.value} ${units}`);
	}
	const timer = node.data.timer as { value?: number; units?: string } | undefined;
	if (timer && Number(timer.value) > 0) {
		const units = timer.units === 'min' ? 'minutes' : 'seconds';
		segments.push(`Timer ${timer.value} ${units}`);
	}
};

/** Action / timer / delay text, then device name (flow canvas preview). */
const buildFinalDeviceActionLabel = (
	node: FlowNode,
	leadParts: string[],
	deviceFull: string,
): string => {
	const parts = [...leadParts];
	appendDelayAndTimerToActionPreview(node, parts);
	if (parts.length === 0) {
		return deviceFull;
	}
	return `${parts.join(' · ')} ${deviceFull}`;
};

const buildIncompleteActionLabelWithTimer = (
	node: FlowNode,
	deviceFull: string,
	actionName: string,
): string => {
	const timerParts: string[] = [];
	appendDelayAndTimerToActionPreview(node, timerParts);
	const incomplete = `${deviceFull} - ${actionName}`;
	if (timerParts.length === 0) {
		return incomplete;
	}
	return `${timerParts.join(' · ')} · ${incomplete}`;
};

/**
 * Generate label for Action node
 */
export function generateActionLabel(node: FlowNode): string {
	// Check for manual override
	if (node.data.isLabelManuallyOverridden && node.data.displayLabel) {
		return node.data.displayLabel;
	}

	// Get device info
	const deviceId = node.data.sceneCapability?.deviceId || node.data.device?.id;
	const channel = node.data.sceneCapability?.channel || node.data.device?.channel;
	const deviceName = getDeviceName(deviceId, channel);
	const channelSuffix = getChannelSuffix(channel);
	const deviceFull = `${deviceName}${channelSuffix}`;

	// If no device selected, return placeholder
	if (!deviceId) {
		return 'Select action';
	}

	// Get action info from sceneCapability or actions array
	let actionName: string | null = null;
	let actionValue: any = null;

	if (node.data.sceneCapability) {
		actionName = node.data.sceneCapability.capabilityName || node.data.sceneCapability.name;
		actionValue = node.data.sceneCapability.parameters?.value ?? node.data.sceneCapability.value;
	} else if (Array.isArray(node.data.actions) && node.data.actions.length > 0) {
		const actions = node.data.actions as ActionItem[];
		const segments = buildDeviceActionPreviewSegments(actions, deviceId, channel);

		if (segments.length > 0) {
			return buildFinalDeviceActionLabel(node, segments, deviceFull);
		}

		const action = actions[0];
		actionName = action.name;
		actionValue = action.value;
	}

	// If only device selected (or no structured action), still show delay / timer when set
	if (!actionName) {
		return buildFinalDeviceActionLabel(node, [], deviceFull);
	}

	// If no value selected, return device + action name
	if (actionValue === undefined || actionValue === null) {
		return buildIncompleteActionLabelWithTimer(node, deviceFull, actionName);
	}

	// Generate full label: "Verb Device" format
	const actionDescription = getActionDescription(actionName, actionValue, deviceId, channel);
	return buildFinalDeviceActionLabel(node, [actionDescription], deviceFull);
}

/**
 * Centralized helper: build verbose condition label from condition data.
 * Uses i18n templates when available for human-readable, translatable strings.
 */
export function conditionToVerboseLabel(input: {
	deviceId?: string | null;
	deviceName?: string;
	channel?: number | string;
	propertyName: string;
	operator?: string;
	value?: any;
}): string {
	const deviceName =
		input.deviceName ?? (input.deviceId ? getDeviceName(input.deviceId, input.channel) : '');
	if (!deviceName && !input.deviceId) return 'Select condition';

	const channelSuffix = getChannelSuffix(input.channel);
	const prop = String(input.propertyName || '').trim();
	if (!prop) return `${deviceName}${channelSuffix}`;

	const operator = input.operator ?? '';
	const value = input.value;
	if (operator === undefined || operator === null || value === undefined || value === null) {
		return `${deviceName}${channelSuffix} ${prop}`;
	}

	const subject = `${deviceName}${channelSuffix}`.trim();
	const propKey = prop
		.toLowerCase()
		.replace(/[\s_-]+/g, ' ')
		.trim();
	const normalizedValue = String(value ?? '').toLowerCase();
	const isPositive = ['1', 'true', 'on', 'detected', 'active', 'open', 'opened'].includes(
		normalizedValue,
	);
	const isNegative = ['0', 'false', 'off', 'not detected', 'inactive', 'closed'].includes(
		normalizedValue,
	);

	// Prefer event/state language over database-shaped "property operator value" strings.
	if (['motion', 'presence', 'occupancy'].some((name) => propKey.includes(name))) {
		if (isPositive) return `${subject} detects presence`;
		if (isNegative) return `${subject} no longer detects presence`;
	}
	if (['door', 'window', 'contact'].some((name) => propKey.includes(name))) {
		if (isPositive) return `${subject} is open`;
		if (isNegative) return `${subject} is closed`;
	}
	if (['button', 'input'].some((name) => propKey.includes(name)) && isPositive) {
		return `${subject} is pressed`;
	}
	if (propKey === 'turn' || propKey === 'state' || propKey.startsWith('turn ')) {
		if (isPositive) return `${subject} is on`;
		if (isNegative) return `${subject} is off`;
	}

	const template = getConditionTemplate(deviceName, prop, operator, value, input.channel);
	return (
		_format(template.key, {
			values: template.values,
			default: template.default,
		}) || template.default
	);
}

/**
 * Get condition operator description
 */
function getOperatorDescription(operator: string): string {
	switch (operator) {
		case 'eq':
		case 'equal':
			return _format('condition_operator_is', { default: 'is' });
		case 'lt':
		case 'less_than':
			return _format('condition_operator_less_than', { default: 'is below' });
		case 'gt':
		case 'more_than':
			return _format('condition_operator_more_than', { default: 'is above' });
		default:
			return 'is';
	}
}

/**
 * Format condition value
 */
function formatConditionValue(propertyName: string, value: any): string {
	const prop = String(propertyName || '').toLowerCase();
	const normalized = String(value ?? '').toLowerCase();

	if (prop === 'state' || prop === 'turn' || prop.startsWith('turn')) {
		if (normalized === '1' || normalized === 'true' || normalized === 'on') return 'On';
		if (normalized === '0' || normalized === 'false' || normalized === 'off') return 'Off';
	}

	switch (propertyName) {
		case 'turn':
		case 'state':
			return _format(`when_turn_${value}`, { default: value });

		case 'motion':
		case 'vibration':
		case 'smoke':
		case 'flood':
			return _format(value === 'detected' ? 'when_detected' : 'when_not_detected', {
				default: value,
			});

		case 'door':
		case 'window':
			return _format(`door_${value}`, { default: value });

		case 'temperature':
		case 'humidity':
		case 'illuminance':
		case 'lux':
		case 'battery':
			return `${value}`;

		default:
			return String(value);
	}
}

/**
 * Generate label for Condition node
 */
export function generateConditionLabel(node: FlowNode): string {
	// Check for manual override
	if (node.data.isLabelManuallyOverridden && node.data.displayLabel) {
		return node.data.displayLabel;
	}

	const dcForDevice = node.data._device_condition as any;
	const deviceId =
		node.data.sceneCapability?.deviceId || dcForDevice?.device?.id || node.data.device?.id;
	const channel =
		node.data.sceneCapability?.channel || dcForDevice?.device?.channel || node.data.device?.channel;
	const deviceName = getDeviceName(deviceId, channel);
	const channelSuffix = getChannelSuffix(channel);

	// If no device selected
	if (!deviceId) {
		return 'Select condition';
	}

	// Get property info (sceneCapability, _device_condition, or top-level)
	const dc = node.data._device_condition as any;
	const cap = node.data.sceneCapability;
	let propertyName: string | null =
		cap?.capabilityName ||
		cap?.name ||
		dc?.cond_name ||
		node.data.cond_name ||
		node.data.property?.id;
	let operator: string | null =
		cap?.operator || dc?.operator || dc?.cmpop || node.data.operator || node.data.cmpop;
	let value: any =
		cap?.parameters?.value ??
		cap?.value ??
		dc?.value ??
		dc?.cmpval ??
		node.data.value ??
		node.data.cmpval;

	const phrase = conditionToVerboseLabel({
		deviceId,
		deviceName,
		channel,
		propertyName: propertyName ?? '',
		operator: operator ?? undefined,
		value,
	});
	return phrase === 'Select condition' || /^when\b/i.test(phrase) ? phrase : `When ${phrase}`;
}

/**
 * Generate label for Trigger node
 */
export function generateTriggerLabel(node: FlowNode): string {
	// Check for manual override
	if (node.data.isLabelManuallyOverridden && node.data.displayLabel) {
		return node.data.displayLabel;
	}

	const triggerType = node.data.triggerType;

	// Time-based triggers
	if (triggerType === 'time') {
		const timeConfig = node.data.timeConfig;

		if (!timeConfig) {
			return 'Select time trigger';
		}

		switch (timeConfig.type) {
			case 'daily':
				return _format('trigger_daily_at', {
					values: { time: timeConfig.time || '00:00' },
					default: `Every day at ${timeConfig.time || '00:00'}`,
				});
			case 'timer':
				return _format('trigger_after_interval', {
					values: { interval: timeConfig.interval || 0 },
					default: `After ${timeConfig.interval || 0} minutes`,
				});
			case 'once':
				return _format('trigger_once_at', {
					values: { time: timeConfig.time || '00:00' },
					default: `Once at ${timeConfig.time || '00:00'}`,
				});
			default:
				return 'Time trigger';
		}
	}

	// Device-based triggers
	if (triggerType === 'device') {
		const deviceId = node.data.sceneCapability?.deviceId || node.data.device?.id;
		const channel = node.data.sceneCapability?.channel || node.data.device?.channel;
		const deviceName = getDeviceName(deviceId, channel);
		const channelSuffix = getChannelSuffix(channel);

		if (!deviceId) {
			return 'Select trigger';
		}

		let propertyName: string | null = null;
		let value: any = null;

		if (node.data.sceneCapability) {
			propertyName = node.data.sceneCapability.capabilityName || node.data.sceneCapability.name;
			value = node.data.sceneCapability.parameters?.value ?? node.data.sceneCapability.value;
		} else {
			propertyName = node.data.property?.id;
			value = node.data.value;
		}

		if (!propertyName) {
			return `When ${deviceName}${channelSuffix}`;
		}

		const conditionType = node.data.conditionType || node.data.sceneCapability?.conditionType;
		let executionType = '';

		if (conditionType) {
			if (conditionType.id === 1) {
				const duration = conditionType.valid_for_min || 5;
				executionType = ` (once for ${duration} min)`;
			} else if (conditionType.id === 2) {
				executionType = ' (on any change)';
			} else if (conditionType.id === 3) {
				executionType = ' (repeatedly)';
			}
		}

		if (value === undefined || value === null) {
			return `When ${deviceName}${channelSuffix} ${propertyName}${executionType}`;
		}

		const valueDesc = formatConditionValue(propertyName, value);
		return `When ${deviceName}${channelSuffix} ${propertyName} ${valueDesc}${executionType}`;
	}

	// Default
	return _format('trigger_manual', { default: 'Manual trigger' });
}

/**
 * Generate label for Delay node
 */
export function generateTimerLabel(node: FlowNode): string {
	if (node.data.isLabelManuallyOverridden && node.data.displayLabel) {
		return node.data.displayLabel;
	}

	const timerMode = (node.data.timerMode as string) || 'delay';

	if (timerMode === 'waitFor') {
		return node.data.displayLabel || 'Wait for event';
	}

	const delay = node.data.delay;
	if (!delay || !(delay as any).value) {
		return 'Set delay';
	}

	const value = (delay as any).value;
	const units = (delay as any).units === 'min' ? 'minutes' : 'seconds';

	return `Wait ${value} ${units}`;
}

export function generateLogicGateLabel(node: FlowNode): string {
	const gateType = (node.data.gateType as string) || 'or';
	const labels: Record<string, string> = {
		or: 'Continue when any input matches',
		and: 'Continue when every input matches',
		nor: 'Continue when no inputs match',
	};
	return labels[gateType] || 'Combine incoming conditions';
}

export function generateNotificationLabel(node: FlowNode): string {
	if (node.data.isLabelManuallyOverridden && node.data.displayLabel) {
		return node.data.displayLabel;
	}
	const presentation = resolveNotificationCardPresentation(node.data);
	return presentation.title === presentation.kindLabel
		? presentation.kindLabel
		: `${presentation.kindLabel}: ${presentation.title}`;
}

const WEATHER_COND_LABELS: Record<string, string> = {
	temperature: 'Temperature',
	humidity: 'Humidity',
	clouds: 'Cloud Coverage',
	wind_speed: 'Wind Speed',
	wind_force: 'Wind Force',
	wind_direction: 'Wind Direction',
	chance_precipitation: 'Precipitation',
};

const WEATHER_OP_LABELS: Record<string, string> = {
	equal: '=',
	eq: '=',
	greater_than: '>',
	gt: '>',
	more_than: '>',
	less_than: '<',
	lt: '<',
};

export function generateWeatherLabel(node: FlowNode): string {
	const d = node.data as Record<string, unknown>;
	const cond = String(d.weatherCond ?? d.cond ?? 'temperature');
	const op = String(d.operator ?? d.op ?? 'equal');
	const value = d.value ?? '';
	const hourOffset = Number(d.hourOffset ?? d.hours ?? 0);

	const condLabel = WEATHER_COND_LABELS[cond] ?? cond;
	const opLabel = WEATHER_OP_LABELS[op] ?? '=';
	const timeLabel = hourOffset === 0 ? 'now' : `+${hourOffset}h`;

	if (value === '' || value === null || value === undefined) {
		return `Weather: ${condLabel}`;
	}
	return `${condLabel} ${opLabel} ${value} (${timeLabel})`;
}

function getSceneMetaName(sceneId: string | number | undefined | null): string {
	if (sceneId == null || sceneId === '') return 'Scene';
	const m = get(scenes) as Record<string, any>;
	const key = String(sceneId);
	const s = m[key];
	return s?._meta?.name || 'Scene';
}

function getGroupMetaName(groupId: string | number | undefined | null): string {
	if (groupId == null || groupId === '') return 'Group';
	const m = get(groups) as Record<string, any>;
	const idNum = typeof groupId === 'number' ? groupId : parseInt(String(groupId), 10);
	const g = m[groupId] ?? m[idNum];
	return g?.name || 'Group';
}

/** Run / stop device script action. */
export function generateScriptActionLabel(node: FlowNode): string {
	if (node.data.isLabelManuallyOverridden && node.data.displayLabel) {
		return String(node.data.displayLabel);
	}
	const commandLabels: Record<string, string> = {
		start: 'Run',
		stop: 'Stop',
		enable: 'Enable',
		disable: 'Disable',
	};
	const command = commandLabels[String(node.data.command)] ?? 'Run';
	const scriptLabel = node.data.scriptName
		? String(node.data.scriptName)
		: node.data.scriptId != null
			? `script ${node.data.scriptId}`
			: 'script';
	const deviceLabel = String(node.data.deviceName || node.data.deviceRealId || 'device');
	return `${command} ${scriptLabel} on ${deviceLabel}`;
}

/** Human-readable scene or alarm action. */
export function generateSceneActionLabel(node: FlowNode): string {
	if (node.data.isLabelManuallyOverridden && node.data.displayLabel) {
		return String(node.data.displayLabel);
	}
	const isAlarm = node.type === 'alarmAction' || node.data.filterType === 'alarm';
	const action = String(node.data.actionType ?? 'play');
	const sceneName = String(
		node.data.sceneName || getSceneMetaName(node.data.sceneId as string | number | undefined),
	);
	let actionLabel = '';
	switch (action) {
		case 'play':
			actionLabel = isAlarm ? 'Trigger' : 'Run';
			break;
		case 'enable':
			actionLabel = 'Enable';
			break;
		case 'disable':
			actionLabel = 'Disable';
			break;
		case 'toggle':
			actionLabel = 'Toggle';
			break;
		default:
			actionLabel = action;
	}
	return `${actionLabel} ${sceneName}`;
}

/** Human-readable group command, including its configured value. */
export function generateGroupActionLabel(node: FlowNode): string {
	if (node.data.isLabelManuallyOverridden && node.data.displayLabel) {
		return String(node.data.displayLabel);
	}
	const groupName = String(
		node.data.groupName || getGroupMetaName(node.data.groupId as string | number | undefined),
	);
	const groupAction = String(node.data.action ?? 'on');
	const rollerPosition = Number(node.data.rollerPosition ?? 100);
	const brightness = Number(node.data.brightness ?? 100);
	const temperature = Number(node.data.thermostatTemperature ?? 21);
	const labels: Record<string, string> = {
		on: 'Turn on',
		off: 'Turn off',
		toggle: 'Toggle',
		open: 'Open',
		close: 'Close',
		stop: 'Stop',
		position: `Move to ${rollerPosition}%`,
		set_brightness: `Set brightness to ${brightness}% on`,
		enable: 'Enable',
		disable: 'Disable',
		set_temperature: `Set target to ${temperature}°C on`,
	};
	const verb = labels[groupAction] || groupAction;
	return `${verb} ${groupName}`;
}

export function generateSwitchNodeLabel(node: FlowNode): string {
	if (node.data.isLabelManuallyOverridden && node.data.displayLabel) {
		return String(node.data.displayLabel);
	}
	const devName = (node.data.deviceName as string) || '';
	const prop = (node.data.property as string) || (node.data.sourceLabel as string) || '';
	const cases = (node.data.cases as unknown[])?.length ?? 0;
	if (devName && prop) {
		return cases > 0
			? `Choose a path from ${devName} ${prop} · ${cases} cases`
			: `Choose a path from ${devName} ${prop}`;
	}
	if (devName) return `Choose a path from ${devName}`;
	return 'Choose a path';
}

/**
 * Label for UI chrome (panel headers, etc.): manual override wins; otherwise
 * regenerate from current stores so device names replace raw ids after $devices hydrates.
 */
export const getEffectiveNodeDisplayLabel = (node: FlowNode): string => {
	if (node.data?.isLabelManuallyOverridden && node.data?.displayLabel) {
		return String(node.data.displayLabel);
	}
	return generateNodeLabel(node);
};

/**
 * Generate label for any node type
 */
export function generateNodeLabel(node: FlowNode): string {
	switch (node.type) {
		case 'action':
			return generateActionLabel(node);
		case 'condition':
			return generateConditionLabel(node);
		case 'mathCondition': {
			const expression = String(
				node.data.expression || node.data.sceneData?._gui_math_expression || '',
			).trim();
			return expression ? `When ${expression}` : 'Configure math expression';
		}
		case 'trigger':
			return generateTriggerLabel(node);
		case 'timer':
			return generateTimerLabel(node);
		case 'logicGate':
			return generateLogicGateLabel(node);
		case 'notificationAction':
			return generateNotificationLabel(node);
		case 'weatherCondition':
			return generateWeatherLabel(node);
		case 'setVariable':
			return node.data.variable?.name ? `Set ${node.data.variable.name}` : 'Set variable';
		case 'virtualComponentEvent': {
			const eventLabels: Record<string, string> = {
				S: 'Single press',
				SS: 'Double press',
				SSS: 'Triple press',
				L: 'Long press',
			};
			return node.data.component?.name
				? `${eventLabels[String(node.data.event)] || 'Press'} on ${node.data.component.name}`
				: 'Choose a virtual button';
		}
		case 'virtualComponentCondition':
			return node.data.component?.name
				? `${node.data.component.name} ${node.data.operator || '='} ${String(node.data.value ?? '')}`
				: 'Choose a virtual component';
		case 'virtualComponentAction':
			if (!node.data.component?.name)
				return node.data.paletteKey === 'emitVirtualButtonEvent'
					? 'Choose a virtual button'
					: 'Choose a virtual component';
			return node.data.paletteKey === 'emitVirtualButtonEvent'
				? `Emit ${String(node.data.event || 'S')} on ${node.data.component.name}`
				: `Set ${node.data.component.name} to ${String(node.data.value ?? '')}`;
		case 'sceneAction':
		case 'alarmAction':
			return generateSceneActionLabel(node);
		case 'groupAction':
			return generateGroupActionLabel(node);
		case 'scriptAction':
			return generateScriptActionLabel(node);
		case 'switchNode':
			return generateSwitchNodeLabel(node);
		case 'variableSwitch':
			return node.data.variableName ? `Switch on ${node.data.variableName}` : 'Choose a variable';
		case 'hysteresisCondition':
			return node.data.property ? `Hysteresis: ${node.data.property}` : 'Configure hysteresis';
		case 'actionSequence': {
			const count = Array.isArray(node.data.steps) ? node.data.steps.length : 0;
			return count ? `${count}-step sequence` : 'Add sequence steps';
		}
		default:
			return node.data.label || 'Node';
	}
}
